import type { Installer } from '../../model';

/*
 * Version helpers to flag devices whose LCS/EMS is behind the latest installer.
 * `versionName` is free-form admin text, so we extract the numeric version token
 * (e.g. "3.8.24") from each side and compare segment by segment.
 */

export const parseVersion = (value?: string): number[] | undefined => {
  const match = value?.match(/\d[\d.]*/);

  return match
    ? match[0]
        .split('.')
        .filter((part) => part !== '')
        .map(Number)
    : undefined;
};

export const compareVersions = (a: number[], b: number[]): number => {
  const length = Math.max(a.length, b.length);

  for (let i = 0; i < length; i++) {
    const diff = (a[i] ?? 0) - (b[i] ?? 0);

    if (diff !== 0) {
      return diff < 0 ? -1 : 1;
    }
  }

  return 0;
};

// Latest available version (highest semver) for a software type across installers.
export const latestVersion = (
  installers: Installer[] | undefined,
  software: 'EMS' | 'LCS'
): string | undefined => {
  let best: { name: string; parsed: number[] } | undefined;

  for (const installer of installers ?? []) {
    if (installer.software !== software) {
      continue;
    }

    const parsed = parseVersion(installer.versionName);

    if (parsed && (!best || compareVersions(parsed, best.parsed) > 0)) {
      best = { name: installer.versionName, parsed };
    }
  }

  return best?.name;
};

// True only when both versions parse and `current` is strictly older than `latest`.
export const isOutdated = (current?: string, latest?: string): boolean => {
  const currentParsed = parseVersion(current);
  const latestParsed = parseVersion(latest);

  return !!currentParsed && !!latestParsed && compareVersions(currentParsed, latestParsed) < 0;
};
